// MT5 runtime card.
//
// Behaviour:
//   - When no runtime exists for this profile (status === "not_started"),
//     shows a credentials form. Form values live ONLY in component state
//     (useState) — refreshing the browser clears them by design. The
//     user must re-enter creds every time they want to spin up a new
//     pod, but they never have to deal with stored credentials.
//   - When a runtime IS running (any other status), shows a status pill
//     + Stop button + Logs + Download EA (last two are admin-only).
//
// Status updates flow through WS (`mt5Runtime.stateChanged` events from
// the orchestrator). The WS gateway replays the latest cached state on
// subscribe + on reconnect, and an initial REST fetch on mount fills the
// gap before the WS hydration arrives. The orchestrator's /status REST
// endpoint is also still available for one-off queries.
import { useEffect, useRef, useState } from "react";
import {
	ApiError,
	httpClient,
	type Mt5RuntimeStartInput,
	type Mt5RuntimeStatus,
	type Mt5RuntimeStatusReport,
} from "../../client/http-client";
import { wsClient } from "../../client/ws-client";
import { PlayIcon, RefreshIcon, StopIcon } from "../../components/Icons";
import { useLiveResource } from "../../hooks/useLiveResource";

interface Mt5RuntimeSectionProps {
	profileId: string;
	isAdmin: boolean;
}

const IDLE_STATUSES: Mt5RuntimeStatus[] = ["not_started", "failed"];

export const Mt5RuntimeSection = ({
	profileId,
	isAdmin,
}: Mt5RuntimeSectionProps) => {
	const [error, setError] = useState<string | null>(null);

	// The credentials form lives entirely in this component's state —
	// no localStorage, no Redux, no parent. Refresh = empty.
	const [creds, setCreds] = useState<Mt5RuntimeStartInput>({
		login: "",
		password: "",
		server: "",
		brokerName: "",
	});
	const [submitBusy, setSubmitBusy] = useState(false);
	const [stopBusy, setStopBusy] = useState(false);
	const [downloadBusy, setDownloadBusy] = useState(false);

	// Reset form state on profile switch.
	useEffect(() => {
		setError(null);
		setCreds({ login: "", password: "", server: "", brokerName: "" });
	}, [profileId]);

	// Status hydration + live updates. See hooks/useLiveResource.ts.
	const { value: report, error: fetchError } =
		useLiveResource<Mt5RuntimeStatusReport>({
			key: profileId,
			fetcher: () => httpClient.mt5Runtime.getStatus(profileId),
			subscribe: (onUpdate) =>
				wsClient.onMt5RuntimeChanged((incomingProfileId, next) => {
					if (incomingProfileId === profileId) onUpdate(next);
				}),
		});
	useEffect(() => {
		if (fetchError instanceof ApiError) setError(fetchError.message);
		else if (fetchError != null) setError(String(fetchError));
	}, [fetchError]);

	const status: Mt5RuntimeStatus = report?.status ?? "not_started";
	const isIdle = IDLE_STATUSES.includes(status);

	// Track the previous status so we can surface an info banner when
	// the orchestrator's auto-idle-teardown closes a pod out from under
	// the user. Without this they'd just see the form reappear with no
	// explanation.
	const prevStatusRef = useRef<Mt5RuntimeStatus | null>(null);
	const [autoStopNotice, setAutoStopNotice] = useState<string | null>(null);
	useEffect(() => {
		const prev = prevStatusRef.current;
		if (
			prev !== null &&
			!IDLE_STATUSES.includes(prev) &&
			IDLE_STATUSES.includes(status) &&
			!stopBusy
		) {
			// Server-initiated transition out of running — almost
			// certainly the idle-teardown loop. The Stop button sets
			// stopBusy=true so we skip the notice for user-initiated
			// stops.
			setAutoStopNotice(
				"MT5 was auto-stopped after inactivity. Enter credentials to restart.",
			);
		}
		prevStatusRef.current = status;
	}, [status, stopBusy]);

	const handleStart = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitBusy(true);
		setError(null);
		try {
			await httpClient.mt5Runtime.start(profileId, creds);
			// Wipe password from memory after a successful start (the
			// other fields stay so a retry on error doesn't re-fill them
			// from scratch — password is the most sensitive so it goes
			// first). The orchestrator broadcasts the new state via WS
			// inside its install handler so we'll see the pill flip
			// without any further nudges from here.
			setCreds((c) => ({ ...c, password: "" }));
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setSubmitBusy(false);
		}
	};

	const handleStop = async () => {
		setStopBusy(true);
		setError(null);
		try {
			await httpClient.mt5Runtime.stop(profileId);
			// The orchestrator broadcasts the post-uninstall state via WS;
			// pill flips to "not_started" and the form re-appears.
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setStopBusy(false);
		}
	};

	const handleDownloadEa = async () => {
		setDownloadBusy(true);
		setError(null);
		try {
			await httpClient.mt5Runtime.downloadExpertAdvisor(profileId);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setDownloadBusy(false);
		}
	};

	return (
		<section className="tg-surface rounded-2xl p-4">
			<header className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-3">
					<span
						className={`inline-block h-2.5 w-2.5 rounded-full ${dotClassFor(status)}`}
						aria-hidden="true"
					/>
					<div>
						<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
							MT5 runtime
						</h3>
						<p className="tg-text-muted text-[0.82rem] leading-5">
							{labelFor(status)}
							{report?.message ? ` · ${report.message}` : ""}
						</p>
					</div>
				</div>
				{!isIdle ? (
					<button
						type="button"
						onClick={() => void handleStop()}
						disabled={stopBusy}
						className="tg-btn-secondary tg-text-secondary inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.85rem] font-semibold disabled:cursor-not-allowed disabled:opacity-55"
					>
						<StopIcon className="h-4 w-4" />
						<span>{stopBusy ? "Stopping..." : "Stop"}</span>
					</button>
				) : null}
			</header>

			{isIdle ? (
				<form
					className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2"
					onSubmit={(e) => void handleStart(e)}
				>
					<LabelledInput
						label="Broker name"
						placeholder="e.g. Vantage Markets"
						value={creds.brokerName}
						onChange={(v) => setCreds((c) => ({ ...c, brokerName: v }))}
						autoComplete="off"
					/>
					<LabelledInput
						label="Server"
						placeholder="e.g. VantageMarkets-Demo"
						value={creds.server}
						onChange={(v) => setCreds((c) => ({ ...c, server: v }))}
						autoComplete="off"
					/>
					<LabelledInput
						label="Login"
						placeholder="numeric account ID"
						value={creds.login}
						onChange={(v) => setCreds((c) => ({ ...c, login: v }))}
						inputMode="numeric"
						autoComplete="off"
					/>
					<LabelledInput
						label="Password"
						placeholder="broker account password"
						value={creds.password}
						onChange={(v) => setCreds((c) => ({ ...c, password: v }))}
						type="password"
						autoComplete="new-password"
					/>
					<div className="md:col-span-2 flex items-center justify-between gap-2 pt-1">
						<p className="tg-text-muted text-[0.78rem] leading-4">
							Credentials are used to spin up your MT5 runtime once and never
							stored. They'll need to be re-entered next time.
						</p>
						<button
							type="submit"
							disabled={
								submitBusy ||
								!creds.brokerName ||
								!creds.server ||
								!creds.login ||
								!creds.password
							}
							className="tg-btn-primary inline-flex h-9 items-center gap-2 rounded-xl px-4 text-[0.85rem] font-semibold disabled:cursor-not-allowed disabled:opacity-55"
						>
							<PlayIcon className="h-4 w-4" />
							<span>{submitBusy ? "Starting..." : "Start MT5"}</span>
						</button>
					</div>
				</form>
			) : null}

			{!isIdle && isAdmin ? (
				<div className="mt-4 border-t border-[color:var(--color-tg-line)] pt-3">
					<div className="flex items-center justify-between gap-2">
						<span className="tg-text-muted text-[0.82rem] leading-5">
							Admin tools
						</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => void handleDownloadEa()}
								disabled={downloadBusy}
								className="tg-btn-secondary tg-text-secondary inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.82rem] font-semibold disabled:opacity-55"
							>
								<RefreshIcon className="h-4 w-4" />
								<span>
									{downloadBusy ? "Downloading..." : "Download EA"}
								</span>
							</button>
						</div>
					</div>
					<Mt5RuntimeAdminLogs profileId={profileId} />
				</div>
			) : null}

			{autoStopNotice && isIdle ? (
				<div
					className="tg-alert-info mt-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-[0.85rem]"
					role="status"
				>
					<span>{autoStopNotice}</span>
					<button
						type="button"
						onClick={() => setAutoStopNotice(null)}
						className="tg-text-muted text-[0.78rem] font-semibold"
					>
						Dismiss
					</button>
				</div>
			) : null}

			{error ? (
				<div
					className="tg-alert-error mt-3 rounded-lg px-3 py-2 text-[0.85rem]"
					role="alert"
				>
					{error}
				</div>
			) : null}
		</section>
	);
};

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

function labelFor(status: Mt5RuntimeStatus): string {
	switch (status) {
		case "not_started":
			return "Not running";
		case "pending":
			return "Pod starting";
		case "booting":
			return "Booting — broker login + EA attach";
		case "broker_authorized":
			return "Broker connected, attaching EA";
		case "polling":
			return "Live — EA polling";
		case "stale":
			return "EA not responding";
		case "failed":
			return "Failed";
		default:
			return status;
	}
}

function dotClassFor(status: Mt5RuntimeStatus): string {
	switch (status) {
		case "polling":
			return "tg-status-dot-success";
		case "pending":
		case "booting":
		case "broker_authorized":
			return "tg-status-dot-warning";
		case "stale":
		case "failed":
			return "tg-status-dot-error";
		default:
			return "tg-status-dot-muted";
	}
}

interface LabelledInputProps {
	label: string;
	value: string;
	onChange: (next: string) => void;
	placeholder?: string;
	type?: "text" | "password";
	inputMode?: "numeric" | "text";
	autoComplete?: string;
}

const LabelledInput = ({
	label,
	value,
	onChange,
	placeholder,
	type = "text",
	inputMode,
	autoComplete,
}: LabelledInputProps) => (
	<label className="flex flex-col gap-1">
		<span className="tg-text-muted text-[0.78rem] font-semibold leading-4">
			{label}
		</span>
		<input
			type={type}
			inputMode={inputMode}
			autoComplete={autoComplete}
			value={value}
			placeholder={placeholder}
			onChange={(e) => onChange(e.target.value)}
			className="tg-input rounded-xl px-3 py-2 text-[0.85rem]"
		/>
	</label>
);

// ────────────────────────────────────────────────────────────────────
// Admin: pod-stdout viewer. Inline-only — the LogViewer above is
// for backend logs; this one tails the MT5 pod stdout via the
// orchestrator's admin-only /mt5-runtime/logs endpoint.
// ────────────────────────────────────────────────────────────────────

interface Mt5RuntimeAdminLogsProps {
	profileId: string;
}

const Mt5RuntimeAdminLogs = ({ profileId }: Mt5RuntimeAdminLogsProps) => {
	const [logs, setLogs] = useState<string>("");
	const [busy, setBusy] = useState(false);
	const [autoRefresh, setAutoRefresh] = useState(false);
	const intervalRef = useRef<number | null>(null);

	const fetchLogs = async () => {
		setBusy(true);
		try {
			const { logs: text } = await httpClient.mt5Runtime.getLogs(
				profileId,
				500,
			);
			setLogs(text);
		} catch (err) {
			setLogs(
				`(failed to fetch logs: ${err instanceof ApiError ? err.message : String(err)})`,
			);
		} finally {
			setBusy(false);
		}
	};

	useEffect(() => {
		if (!autoRefresh) {
			if (intervalRef.current !== null) {
				window.clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}
		void fetchLogs();
		intervalRef.current = window.setInterval(() => void fetchLogs(), 5_000);
		return () => {
			if (intervalRef.current !== null) {
				window.clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoRefresh, profileId]);

	return (
		<div className="mt-3">
			<div className="flex items-center justify-between gap-2">
				<span className="tg-text-muted text-[0.78rem] leading-4">
					Pod logs (last 500 lines)
				</span>
				<div className="flex items-center gap-2">
					<label className="tg-text-muted inline-flex items-center gap-1 text-[0.78rem]">
						<input
							type="checkbox"
							checked={autoRefresh}
							onChange={(e) => setAutoRefresh(e.target.checked)}
						/>
						<span>Auto-refresh</span>
					</label>
					<button
						type="button"
						onClick={() => void fetchLogs()}
						disabled={busy}
						className="tg-btn-secondary tg-text-secondary inline-flex h-8 items-center rounded-lg px-2 text-[0.78rem] font-semibold disabled:opacity-55"
					>
						{busy ? "Loading..." : "Fetch"}
					</button>
				</div>
			</div>
			<pre className="tg-text-secondary mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-[color:var(--color-tg-bg-deep)] p-3 font-mono text-[0.72rem] leading-4">
				{logs || "(no logs fetched yet — click Fetch or Auto-refresh)"}
			</pre>
		</div>
	);
};
